import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";

type AdminUser = {
  kullaniciAdi: string;
  token: string;
};

type Ctx = {
  isAuthed: boolean;
  loading: boolean;
  user?: AdminUser | null;
  login: (kullaniciAdi: string, sifre: string) => Promise<void>;
  logout: () => void;
};

const AdminAuthContext = createContext<Ctx>({
  isAuthed: false,
  loading: true,
  user: null,
  login: async () => {},
  logout: () => {},
});

const TOKEN_KEY = "admin_token";
const USER_KEY = "admin_user";

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthed, setIsAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthed(true);
    }
    setLoading(false);
  }, []);

  const login = async (kullaniciAdi: string, sifre: string) => {
    try {
      const res = await axiosInstance.post("/Auth/adminlogin", { kullaniciAdi, sifre });

      // ✅ API yanıtında success ve token kontrolü
      const data = res.data;
      if (!data?.success) throw new Error(data?.errorDetail || "Giriş başarısız.");

      const token = data?.data?.token || data?.data?.Token;
      if (!token) throw new Error("Token alınamadı.");

      const user: AdminUser = { kullaniciAdi, token };

      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setUser(user);
      setIsAuthed(true);
    } catch (err: any) {
      console.error("Giriş hatası:", err);
      throw new Error(err?.response?.data?.errorDetail || "Giriş başarısız.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setIsAuthed(false);
  };

  const value = useMemo(() => ({ isAuthed, loading, user, login, logout }), [isAuthed, loading, user]);
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => useContext(AdminAuthContext);
